/**
 * Predictive Models → detail → "Field mapping" section.
 *
 * The bridge between the customer's ontology and the per-tenant
 * propensity mart. Workflow as the admin sees it:
 *
 *   1. The backend has run a 3-stage discovery pipeline (exact match
 *      → embedding shortlist → LLM rerank). This panel renders the
 *      result as a table — one row per mart column, with a stage
 *      badge and the proposed source.
 *   2. Admin reviews each row. If a suggestion looks wrong, they
 *      click the pencil to open the OntologyBrowserDialog and pick a
 *      different canonical field. Clear (×) drops the source entirely.
 *   3. When happy, admin clicks "Approve mapping" — the backend
 *      freezes it (`mapping_status='approved'`) and unlocks the
 *      "Start sync" button. The other sections of the detail page
 *      (segments, validation, approve) gate on this same status.
 *   4. "Start sync" kicks off a (currently stubbed) ETL — the
 *      backend flips status to RUNNING and a background task flips
 *      it to SUCCESS after 2s. We poll while running.
 *
 * Streaming discovery (Phase 1.5)
 * -------------------------------
 * Discovery used to run synchronously inside GET /mapping — with a slow
 * reasoning LLM and ~20 mart columns the panel sat blank for a minute.
 * We now:
 *
 *  - Fire POST /mapping/discover when there's no saved mapping. The
 *    backend returns immediately with `discovery_progress.all_columns`
 *    and a worker starts running.
 *  - Render the skeleton table from `all_columns` on first paint —
 *    the admin sees the structure within ~100ms.
 *  - Poll GET /mapping every 750ms; as the worker streams partial
 *    mappings into `discovery_progress.partial_mappings`, individual
 *    rows fade in from skeleton to real content.
 *  - When `field_mapping` lands and `discovery_progress` clears, we
 *    stop polling and render the normal table.
 *
 * Design notes
 * ------------
 * - We keep an in-memory "draft" copy of the mappings the admin is
 *   editing. Saves are batched on explicit click ("Save changes" /
 *   "Approve mapping") rather than per-row, because mid-edit the
 *   table is in an inconsistent state and partial saves would have
 *   to be reasoned about. The UI shows a "You have unsaved
 *   changes" affordance when local state diverges from server state.
 * - Stage badge colours track confidence loosely:
 *     exact     → success (free deterministic win)
 *     llm_rerank → primary (auto-suggested with reasoning)
 *     embedding → warning (LLM call failed; embedding-only fallback)
 *     manual    → info (admin-curated)
 *     no_match  → error (nothing was found; admin must fix)
 * - Multi-source rows show the primary inline + a "+N sources" chip;
 *   the strategy (single / union / coalesce) is rendered as a
 *   secondary chip. The full list is in the row's tooltip so we
 *   don't bloat the row height.
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import LockOpenRounded from '@mui/icons-material/LockOpenRounded';
import SettingsBackupRestoreRounded from '@mui/icons-material/SettingsBackupRestoreRounded';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import {
  approveFieldMapping,
  DiscoveryProgress,
  FieldMappingResponse,
  getFieldMapping,
  MappingSourceRead,
  MappingStage,
  MartColumnMappingRead,
  rerunMappingDiscovery,
  startMappingDiscovery,
  startMappingSync,
  SyncStatus,
  unfreezeFieldMapping,
  updateFieldMapping,
} from '../../../services/propensityService';
import OntologyBrowserDialog from './OntologyBrowserDialog';

interface Props {
  tenantId: string;
  configId: string;
  /** Notify the parent when the mapping/sync state changes so it can
   * gate other sections (segments / validation / approve) behind
   * `mapping_status === 'approved'` and `sync_status === 'success'`. */
  onMappingChanged?: (resp: FieldMappingResponse) => void;
  /** Whether the current user is an admin. We disable mutations for
   * non-admins (read-only view) — the backend rejects them anyway,
   * but the UI shouldn't dangle the buttons. */
  canEdit: boolean;
}

const STAGE_COLOR: Record<MappingStage, 'success' | 'primary' | 'warning' | 'info' | 'error'> = {
  exact: 'success',
  llm_rerank: 'primary',
  embedding: 'warning',
  manual: 'info',
  no_match: 'error',
};

const STAGE_LABEL: Record<MappingStage, string> = {
  exact: 'Exact',
  llm_rerank: 'AI',
  embedding: 'Vector',
  manual: 'Manual',
  no_match: 'Unmapped',
};

const SYNC_COLOR: Record<SyncStatus, 'success' | 'info' | 'warning' | 'error'> = {
  success: 'success',
  running: 'info',
  pending: 'info',
  failed: 'error',
};

// How often we re-fetch /mapping while a discovery is running.
// 750ms gives ~1 row update per poll for a typical 8-way LLM pool;
// faster polling barely helps (rows arrive in bursts as the pool
// drains) but doubles the request volume against the backend.
const DISCOVERY_POLL_MS = 750;

// Hard ceiling on a discovery run before the UI surfaces a "stuck"
// affordance and offers retry. Generous — a 30-column mart with a
// slow reasoning LLM and a cold field-card index can legitimately
// take ~60s. 120s is enough headroom for the long tail without
// keeping a clearly-broken run hidden indefinitely.
const DISCOVERY_TIMEOUT_MS = 120_000;

// Same poll cadence we used pre-streaming for the post-mapping sync.
const SYNC_POLL_MS = 750;
const SYNC_TIMEOUT_MS = 30_000;

export default function FieldMappingPanel({
  tenantId, configId, onMappingChanged, canEdit,
}: Props) {
  const { notify } = React.useContext(SnackbarContext);

  const [server, setServer] = React.useState<FieldMappingResponse | null>(null);
  const [draft, setDraft] = React.useState<MartColumnMappingRead[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // OntologyBrowserDialog wiring — `editingColumn` carries which row
  // the user is editing so the dialog can title-bar the column name
  // and we know which row to update on selection.
  const [editingColumn, setEditingColumn] = React.useState<string | null>(null);
  const [confirmRerun, setConfirmRerun] = React.useState(false);
  const [confirmUnfreeze, setConfirmUnfreeze] = React.useState(false);
  const [confirmApproveWithGaps, setConfirmApproveWithGaps] = React.useState(false);

  const onChangeRef = React.useRef(onMappingChanged);
  React.useEffect(() => { onChangeRef.current = onMappingChanged; }, [onMappingChanged]);

  // De-dup concurrent fetches. React StrictMode runs effects twice
  // in dev — without a guard, both invocations race a GET /mapping
  // and (more critically) a POST /mapping/discover, which wastes the
  // user's time. The backend is idempotent on /discover, but
  // skipping the second client call is still a small win.
  const inflightRef = React.useRef(false);

  const applyServerState = React.useCallback((resp: FieldMappingResponse) => {
    setServer(resp);
    setDraft(resp.field_mapping?.mappings ? [...resp.field_mapping.mappings] : null);
    onChangeRef.current?.(resp);
  }, []);

  const refresh = React.useCallback(async (): Promise<FieldMappingResponse | null> => {
    if (inflightRef.current) return null;
    inflightRef.current = true;
    setError(null);
    try {
      const resp = await getFieldMapping(tenantId, configId);
      applyServerState(resp);
      return resp;
    } catch (e: any) {
      setError(e?.message || 'Failed to load field mapping');
      return null;
    } finally {
      inflightRef.current = false;
    }
  }, [tenantId, configId, applyServerState]);

  // ------------------------------------------------------------------
  // Initial mount: fetch current mapping state.
  //
  // We only fetch here — discovery is intentionally NOT auto-started.
  // The admin must click "Run discovery" explicitly. This avoids
  // surprising LLM calls when the detail page is first opened, and
  // lets the admin review the mart columns before committing to a run.
  //
  // If a worker is already in flight (e.g. the panel was closed and
  // re-opened mid-run), the polling effect below will pick it up.
  // ------------------------------------------------------------------
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [tenantId, configId, refresh]);

  // ------------------------------------------------------------------
  // Polling loop (discovery in flight)
  //
  // Active when `discovery_progress.status === 'running'`. Stops when:
  //   - status flips to 'done' or 'failed', OR
  //   - `discovery_progress` is cleared (worker promoted to field_mapping), OR
  //   - elapsed > DISCOVERY_TIMEOUT_MS (defensive — kills a polling
  //     loop attached to a worker that crashed without writing failure).
  //
  // We don't show a spinner per poll because the rows themselves
  // animate in from the skeleton state — that IS the progress signal.
  // ------------------------------------------------------------------
  const isRunning = server?.discovery_progress?.status === 'running';
  React.useEffect(() => {
    if (!isRunning) return;
    let elapsed = 0;
    const timer: ReturnType<typeof setInterval> = setInterval(async () => {
      elapsed += DISCOVERY_POLL_MS;
      try {
        const resp = await getFieldMapping(tenantId, configId);
        applyServerState(resp);
        const dp = resp.discovery_progress;
        if (!dp || dp.status !== 'running' || elapsed > DISCOVERY_TIMEOUT_MS) {
          clearInterval(timer);
        }
      } catch {
        // Swallow — we'll try again next tick. A persistent failure
        // will eventually trip the timeout.
      }
    }, DISCOVERY_POLL_MS);
    return () => clearInterval(timer);
  }, [isRunning, tenantId, configId, applyServerState]);

  // ------------------------------------------------------------------
  // Polling loop (sync in flight) — unchanged behaviour
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (server?.sync_status !== 'running') return;
    let elapsed = 0;
    const timer: ReturnType<typeof setInterval> = setInterval(async () => {
      elapsed += SYNC_POLL_MS;
      try {
        const resp = await getFieldMapping(tenantId, configId);
        if (resp.sync_status !== 'running' || elapsed > SYNC_TIMEOUT_MS) {
          clearInterval(timer);
          applyServerState(resp);
        }
      } catch {
        // See above.
      }
    }, SYNC_POLL_MS);
    return () => clearInterval(timer);
  }, [server?.sync_status, tenantId, configId, applyServerState]);

  // Local-edit derived state (only meaningful when we have a final mapping)
  const dirty = React.useMemo(() => {
    if (!server?.field_mapping?.mappings || !draft) return false;
    return JSON.stringify(server.field_mapping.mappings) !== JSON.stringify(draft);
  }, [server, draft]);

  const counts = React.useMemo(() => {
    if (!draft) return {} as Record<string, number>;
    const out: Record<string, number> = {};
    for (const m of draft) {
      out[m.stage] = (out[m.stage] || 0) + 1;
    }
    return out;
  }, [draft]);

  const unmappedColumns = React.useMemo(() => (
    (draft || []).filter((m) => !m.primary_source).map((m) => m.mart_column)
  ), [draft]);

  // ------------------------------------------------------------------
  // Mutations
  // ------------------------------------------------------------------

  const updateRow = (mart_column: string, patch: Partial<MartColumnMappingRead>) => {
    setDraft((prev) => prev ? prev.map(
      (m) => m.mart_column === mart_column ? { ...m, ...patch } : m,
    ) : prev);
  };

  const handleSelectSource = (source: MappingSourceRead) => {
    if (!editingColumn) return;
    updateRow(editingColumn, {
      primary_source: source,
      stage: 'manual',
      reviewed_by_user: true,
      // Keep existing additional sources only if the entity matches the
      // new primary; otherwise clear them — they were tied to the old
      // primary's narrative reasoning. The admin can re-add multi-source
      // entries afterwards if needed (V2 will surface this as a UI
      // affordance; for now manual edits are single-source).
      additional_sources: [],
      multi_source_strategy: 'single',
    });
  };

  const handleClear = (mart_column: string) => {
    updateRow(mart_column, {
      primary_source: null,
      additional_sources: [],
      stage: 'no_match',
      multi_source_strategy: 'single',
      reviewed_by_user: true,
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    setBusy('save');
    try {
      const resp = await updateFieldMapping(tenantId, configId, draft);
      applyServerState(resp);
      notify('Mapping saved', 'success');
    } catch (e: any) {
      notify(e?.message || 'Save failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async () => {
    if (!draft) return;
    // Soft gate — when the admin is leaving columns unmapped, the
    // sync will fill them with NULL. That's intentional for partial-
    // coverage demos but worth a one-tap confirm so it doesn't slip
    // by. The dialog calls performApprove() once dismissed.
    if (unmappedColumns.length > 0) {
      setConfirmApproveWithGaps(true);
      return;
    }
    await performApprove();
  };

  const performApprove = async () => {
    if (!draft) return;
    setConfirmApproveWithGaps(false);
    if (dirty) {
      // Save the in-progress edits first so the server has the same
      // mapping the admin sees. Otherwise approve uses an out-of-date
      // snapshot.
      try {
        const saved = await updateFieldMapping(tenantId, configId, draft);
        applyServerState(saved);
      } catch (e: any) {
        notify(e?.message || 'Save failed before approve', 'error');
        return;
      }
    }
    setBusy('approve');
    try {
      const resp = await approveFieldMapping(tenantId, configId);
      applyServerState(resp);
      notify('Mapping approved', 'success');
    } catch (e: any) {
      notify(e?.message || 'Approve failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleRerun = async () => {
    setBusy('rerun');
    setConfirmRerun(false);
    try {
      // The /suggest endpoint is async now — it returns 202 with the
      // initial discovery_progress envelope. The polling effect
      // picks up from there.
      const resp = await rerunMappingDiscovery(tenantId, configId);
      applyServerState(resp);
      notify('Re-running discovery', 'info');
    } catch (e: any) {
      notify(e?.message || 'Re-run failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleStartSync = async () => {
    setBusy('sync');
    try {
      const resp = await startMappingSync(tenantId, configId);
      applyServerState(resp);
      notify('Sync started', 'info');
    } catch (e: any) {
      notify(e?.message || 'Sync failed to start', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleUnfreeze = async () => {
    setBusy('unfreeze');
    setConfirmUnfreeze(false);
    try {
      const resp = await unfreezeFieldMapping(tenantId, configId);
      applyServerState(resp);
      notify('Mapping reopened for editing', 'info');
    } catch (e: any) {
      notify(e?.message || 'Failed to reopen mapping', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleRetryDiscovery = async () => {
    setBusy('retry');
    try {
      // /suggest forces a fresh run regardless of state — same code
      // path as the explicit re-run, just without the confirm dialog
      // (the previous run already failed, no edits to lose).
      const resp = await rerunMappingDiscovery(tenantId, configId);
      applyServerState(resp);
    } catch (e: any) {
      notify(e?.message || 'Retry failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (loading && !server) {
    // Brief — only covers the very first GET /mapping before we know
    // whether we have a saved mapping, an in-flight run, or need to
    // schedule a new one. After this we always have something to
    // show (skeleton table at minimum).
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Loading field mapping…
        </Typography>
      </Stack>
    );
  }

  if (error && !server) {
    return (
      <Alert
        severity="error"
        action={<Button size="small" startIcon={<RefreshRounded />} onClick={() => { void refresh(); }}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }

  if (!server) return null;

  // Discovery-failed surface — separate code path so the user gets a
  // clear retry CTA instead of a blank table.
  if (server.discovery_progress?.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            startIcon={<RefreshRounded />}
            onClick={handleRetryDiscovery}
            disabled={busy === 'retry'}
          >
            {busy === 'retry' ? 'Retrying…' : 'Retry'}
          </Button>
        }
      >
        Discovery failed
        {server.discovery_progress.error
          ? <>: <code>{server.discovery_progress.error}</code></>
          : null}
      </Alert>
    );
  }

  const progress = server.discovery_progress;
  const renderingDiscovery = progress?.status === 'running';

  // Discovery streaming view — render skeleton table from
  // `all_columns`, replace rows with real content as they land in
  // `partial_mappings`. Keeps the layout stable so the admin sees
  // structure immediately and rows fade in without reflow.
  if (renderingDiscovery && progress) {
    return (
      <DiscoveryStreamingView
        progress={progress}
        elapsedMs={Date.now() - new Date(progress.started_at).getTime()}
      />
    );
  }

  // Stale state defensively — should never hit since GET /mapping is
  // exhaustive, but better a placeholder than a crash.
  if (!draft || !server.field_mapping) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No mapping available. Click "Re-run discovery" to generate one.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SettingsBackupRestoreRounded />}
          onClick={handleRetryDiscovery}
          disabled={!canEdit || busy === 'retry'}
        >
          Run discovery
        </Button>
      </Stack>
    );
  }

  const isApproved = server.mapping_status === 'approved';
  const ontologyDrifted = (
    server.field_mapping
    && server.latest_ontology_version
    && server.latest_ontology_version !== server.field_mapping.ontology_version
  );

  const syncStatus = server.sync_status;
  const syncRunning = syncStatus === 'running';

  return (
    <Box>
      {/* Header — counts pill + actions */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flex: 1, flexWrap: 'wrap' }}>
          {(Object.keys(STAGE_LABEL) as MappingStage[]).map((stage) => {
            const cnt = counts[stage] || 0;
            if (cnt === 0) return null;
            return (
              <Chip
                key={stage}
                size="small"
                label={`${cnt} ${STAGE_LABEL[stage].toLowerCase()}`}
                color={STAGE_COLOR[stage]}
                variant="outlined"
              />
            );
          })}
        </Stack>
        <Tooltip title="Re-run discovery — clobbers in-progress edits">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsBackupRestoreRounded />}
              disabled={!canEdit || busy === 'rerun' || isApproved}
              onClick={() => setConfirmRerun(true)}
            >
              {busy === 'rerun' ? 'Re-running…' : 'Re-run discovery'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {ontologyDrifted && !isApproved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          New ontology version detected (was{' '}
          <code>{server.field_mapping?.ontology_version}</code>, now{' '}
          <code>{server.latest_ontology_version}</code>). Re-run discovery to
          pick up the latest fields and descriptions.
        </Alert>
      )}

      {isApproved && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          icon={<CheckCircleRounded />}
          action={
            canEdit ? (
              <Tooltip title="Reopen the mapping so you can edit it or re-run discovery">
                <span>
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<LockOpenRounded fontSize="small" />}
                    disabled={busy === 'unfreeze'}
                    onClick={() => setConfirmUnfreeze(true)}
                  >
                    {busy === 'unfreeze' ? 'Reopening…' : 'Reopen for editing'}
                  </Button>
                </span>
              </Tooltip>
            ) : undefined
          }
        >
          Mapping approved. {syncStatus === 'success'
            ? 'Last sync completed successfully.'
            : 'Trigger a sync to land data into the mart.'}
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="22%">Mart column</TableCell>
            <TableCell width="50%">Source</TableCell>
            <TableCell width="15%">Stage</TableCell>
            <TableCell width="13%" align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {draft.map((m) => (
            <MappingRow
              key={m.mart_column}
              mapping={m}
              canEdit={canEdit && !isApproved}
              onEdit={() => setEditingColumn(m.mart_column)}
              onClear={() => handleClear(m.mart_column)}
            />
          ))}
        </TableBody>
      </Table>

      {/* Footer — save / approve / sync */}
      <Stack direction="row" spacing={1} sx={{ mt: 3 }} alignItems="center">
        {!isApproved && dirty && (
          <Button
            variant="outlined"
            disabled={!canEdit || busy === 'save'}
            onClick={handleSave}
          >
            {busy === 'save' ? 'Saving…' : 'Save changes'}
          </Button>
        )}
        {!isApproved && (
          <Tooltip
            title={unmappedColumns.length > 0
              ? `${unmappedColumns.length} column(s) will sync as NULL — you'll be asked to confirm.`
              : ''}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                disabled={!canEdit || busy === 'approve'}
                onClick={handleApprove}
                startIcon={<CheckCircleRounded />}
              >
                {busy === 'approve' ? 'Approving…' : 'Approve mapping'}
              </Button>
            </span>
          </Tooltip>
        )}
        {isApproved && (
          <>
            <Button
              variant="contained"
              color="primary"
              disabled={!canEdit || busy === 'sync' || syncRunning}
              onClick={handleStartSync}
              startIcon={syncRunning ? <CircularProgress size={14} /> : <CloudSyncRounded />}
            >
              {syncRunning
                ? 'Syncing…'
                : syncStatus === 'success' ? 'Re-run sync' : 'Start sync'}
            </Button>
            {syncStatus && (
              <Chip
                size="small"
                label={`sync: ${syncStatus}`}
                color={SYNC_COLOR[syncStatus]}
                variant="outlined"
              />
            )}
            {server.sync_completed_at && (
              <Typography variant="caption" color="text.secondary">
                Last run: {new Date(server.sync_completed_at).toLocaleString()}
              </Typography>
            )}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {dirty && !isApproved && (
          <Typography variant="caption" color="warning.main">
            Unsaved changes
          </Typography>
        )}
      </Stack>

      <OntologyBrowserDialog
        open={editingColumn !== null}
        onClose={() => setEditingColumn(null)}
        onSelect={handleSelectSource}
        tenantId={tenantId}
        martColumnLabel={editingColumn ?? undefined}
      />

      <Dialog open={confirmRerun} onClose={() => setConfirmRerun(false)}>
        <DialogTitle>Re-run discovery?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will rebuild the mapping from scratch using the latest
            ontology and any manual edits in this panel will be discarded.
            This is useful when your ontology has changed but you haven&apos;t
            approved the current mapping yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRerun(false)}>Cancel</Button>
          <Button onClick={handleRerun} color="primary" variant="contained">
            Re-run discovery
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmApproveWithGaps}
        onClose={() => setConfirmApproveWithGaps(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve with unmapped columns?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box component="p" sx={{ m: 0, mb: 1 }}>
              {unmappedColumns.length} column(s) don&apos;t have a source
              mapping. They&apos;ll sync as <code>NULL</code> in the mart:
            </Box>
            <Box
              component="ul"
              sx={{ m: 0, pl: 3, maxHeight: 160, overflowY: 'auto' }}
            >
              {unmappedColumns.slice(0, 20).map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
              {unmappedColumns.length > 20 && (
                <li>…and {unmappedColumns.length - 20} more</li>
              )}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveWithGaps(false)}>Cancel</Button>
          <Button
            onClick={performApprove}
            color="primary"
            variant="contained"
            disabled={busy === 'approve'}
          >
            {busy === 'approve' ? 'Approving…' : 'Approve anyway'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmUnfreeze} onClose={() => setConfirmUnfreeze(false)}>
        <DialogTitle>Reopen mapping for editing?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current mapping will stay exactly as it is — no rows will
            change. We&apos;ll just unlock the panel so you can edit
            individual rows or re-run discovery against a newer ontology.
            <Box component="span" sx={{ display: 'block', mt: 1 }}>
              Already-synced data in the mart is left in place. If you
              change the mapping after reopening, you&apos;ll need to
              approve again and re-run the sync to land the new schema.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnfreeze(false)}>Cancel</Button>
          <Button onClick={handleUnfreeze} color="primary" variant="contained">
            Reopen for editing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Streaming view
//
// Rendered while a discovery worker is in flight. Shows:
//   - A determinate progress bar driven by `partial_mappings.size /
//     all_columns.length` (more honest than a spinner — it reflects
//     actual work done rather than wall time).
//   - The skeleton table with one row per mart column. Each row that
//     hasn't been resolved yet shows shimmer placeholders for source +
//     stage; rows that landed render their real content with a brief
//     fade-in so the eye catches the change.
//
// Crucially we DON'T re-mount this component on every poll — the same
// skeleton rows are reused and only their props change. That keeps
// the skeleton shimmer animation continuous (no jitter) and lets the
// MUI Fade transition fire only on the per-row "skeleton → real"
// transition.
// ---------------------------------------------------------------------------

function DiscoveryStreamingView({
  progress, elapsedMs,
}: {
  progress: DiscoveryProgress;
  elapsedMs: number;
}) {
  const total = progress.all_columns.length;
  const done = Object.keys(progress.partial_mappings || {}).length;
  // Cap at 99% until the worker actually finalises — going to 100%
  // before `field_mapping` lands would feel like a lie when the row
  // doesn't immediately become editable.
  const pct = total > 0 ? Math.min(99, Math.round((done / total) * 100)) : 0;
  const elapsedSec = Math.max(0, Math.round(elapsedMs / 1000));

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Discovering field mappings…
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {done} / {total} columns · {elapsedSec}s elapsed
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 2 }}
      >
        Walking each mart column through the ontology with embeddings and an
        LLM rerank. Rows fill in as they're matched.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width="22%">Mart column</TableCell>
            <TableCell width="50%">Source</TableCell>
            <TableCell width="15%">Stage</TableCell>
            <TableCell width="13%" align="right">&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {progress.all_columns.map((column) => {
            const mapping = progress.partial_mappings?.[column];
            return (
              <SkeletonOrMappingRow
                key={column}
                column={column}
                mapping={mapping}
              />
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function SkeletonOrMappingRow({
  column, mapping,
}: {
  column: string;
  mapping?: MartColumnMappingRead;
}) {
  if (!mapping) {
    return (
      <TableRow>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {column}
          </Typography>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="70%" />
        </TableCell>
        <TableCell>
          <Skeleton variant="rounded" width={50} height={20} />
        </TableCell>
        <TableCell />
      </TableRow>
    );
  }

  // Filled row — fade in the cells (not the row itself) so the
  // surrounding rows stay still and only the new content animates.
  // 350ms is fast enough to feel responsive but slow enough that the
  // eye catches which row just landed.
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {column}
        </Typography>
      </TableCell>
      <TableCell>
        <Fade in timeout={350}>
          <Box>
            {mapping.primary_source ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Tooltip
                  title={mapping.primary_source.reasoning || 'No reasoning recorded'}
                  arrow
                  placement="top"
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {mapping.primary_source.canonical_entity_name}.{mapping.primary_source.canonical_field_name}
                  </Typography>
                </Tooltip>
                {mapping.additional_sources.length > 0 && (
                  <Chip
                    size="small"
                    label={`+${mapping.additional_sources.length} ${mapping.multi_source_strategy}`}
                    color="info"
                    variant="outlined"
                    sx={{ height: 20, fontSize: 11 }}
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No source mapped
              </Typography>
            )}
          </Box>
        </Fade>
      </TableCell>
      <TableCell>
        <Fade in timeout={350}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={STAGE_LABEL[mapping.stage] || mapping.stage}
              color={STAGE_COLOR[mapping.stage] || 'default'}
              sx={{ height: 20, fontSize: 11 }}
            />
            {typeof mapping.primary_source?.confidence === 'number'
              && mapping.primary_source.confidence > 0 && (
              <Typography variant="caption" color="text.secondary">
                {Math.round(mapping.primary_source.confidence * 100)}%
              </Typography>
            )}
          </Stack>
        </Fade>
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Row (final, post-discovery)
// ---------------------------------------------------------------------------

function MappingRow({
  mapping, canEdit, onEdit, onClear,
}: {
  mapping: MartColumnMappingRead;
  canEdit: boolean;
  onEdit: () => void;
  onClear: () => void;
}) {
  const src = mapping.primary_source;
  const additionalCount = mapping.additional_sources.length;
  const conf = src?.confidence;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {mapping.mart_column}
        </Typography>
      </TableCell>
      <TableCell>
        {src ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Tooltip
              title={src.reasoning || 'No reasoning recorded'}
              arrow
              placement="top"
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {src.canonical_entity_name}.{src.canonical_field_name}
              </Typography>
            </Tooltip>
            {additionalCount > 0 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                      Strategy: {mapping.multi_source_strategy}
                    </Typography>
                    {mapping.additional_sources.map((s, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                        + {s.canonical_entity_name}.{s.canonical_field_name}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
              >
                <Chip
                  size="small"
                  label={`+${additionalCount} ${mapping.multi_source_strategy}`}
                  color="info"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No source mapped
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={STAGE_LABEL[mapping.stage] || mapping.stage}
            color={STAGE_COLOR[mapping.stage] || 'default'}
            sx={{ height: 20, fontSize: 11 }}
          />
          {typeof conf === 'number' && conf > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round(conf * 100)}%
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Tooltip title={src ? 'Change source field' : 'Pick a source field'}>
          <span>
            <IconButton
              size="small"
              disabled={!canEdit}
              onClick={onEdit}
              aria-label={src ? 'Change source field' : 'Pick a source field'}
            >
              <EditRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {src && (
          <Tooltip title="Clear source — column will sync as NULL">
            <span>
              <IconButton
                size="small"
                disabled={!canEdit}
                onClick={onClear}
                aria-label="Clear source mapping"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
